import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface Notification {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  user_id: number;
  user_type: string;
  title: string;
  message: string;
  type: string;
  channel: string;
  status: string;
  is_read: boolean;
  read_at: string;
  data: string;
  template_id: number;
  scheduled_at: string;
  sent_at: string;
  fail_reason: string;
  retry_count: number;
  external_id: string;
}

interface NotificationsState {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  loading: false,
  error: null,
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.nms}/api/v1/notifications`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch notifications');
    }
  }
);

export const createNotification = createAsyncThunk(
  'notifications/create',
  async (data: Partial<Notification>, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.nms}/api/v1/notifications`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create notification');
    }
  }
);

export const sendNotification = createAsyncThunk(
  'notifications/send',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.nms}/api/v1/notifications/${id}/send`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to send notification');
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.nms}/api/v1/notifications/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete notification');
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchNotifications.fulfilled, (state, action) => { state.loading = false; state.notifications = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchNotifications.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createNotification.fulfilled, (state, action) => {
        const n = action.payload?.data || action.payload;
        if (n) state.notifications.push(n);
      })
      .addCase(sendNotification.fulfilled, (state, action) => {
        const n = action.payload?.data || action.payload;
        if (n) {
          const idx = state.notifications.findIndex(x => x.ID === n.ID);
          if (idx >= 0) state.notifications[idx] = n;
        }
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.notifications = state.notifications.filter(n => n.ID !== action.payload);
      });
  },
});

export const { clearNotificationsError } = notificationsSlice.actions;
export default notificationsSlice.reducer;
